import { useState, useCallback } from 'react';

/**
 * Hook for showing confirm dialogs. Returns dialog state + a confirm() function.
 *
 * Usage:
 *   const { confirmDialog, confirm } = useConfirm();
 *
 *   // In an event handler:
 *   const ok = await confirm({
 *     title: 'Delete Customer',
 *     message: 'This will permanently delete the customer and all associated jobs.',
 *     variant: 'danger',
 *     confirmLabel: 'Delete'
 *   });
 *   if (!ok) return;
 *   // ... proceed with action
 *
 *   // In JSX — spread confirmDialog onto ConfirmDialog:
 *   <ConfirmDialog {...confirmDialog} />
 */
export default function useConfirm() {
  const [state, setState] = useState({
    open: false,
    title: 'Confirm',
    message: '',
    variant: 'info',
    confirmLabel: 'Confirm',
    cancelLabel: 'Cancel',
    resolve: null
  });

  const confirm = useCallback((opts = {}) => {
    return new Promise((resolve) => {
      setState({
        open: true,
        title: opts.title || 'Confirm',
        message: opts.message || '',
        variant: opts.variant || 'info',
        confirmLabel: opts.confirmLabel || 'Confirm',
        cancelLabel: opts.cancelLabel !== undefined ? opts.cancelLabel : 'Cancel',
        resolve
      });
    });
  }, []);

  const handleConfirm = useCallback(() => {
    state.resolve?.(true);
    setState(prev => ({ ...prev, open: false }));
  }, [state.resolve]);

  const handleCancel = useCallback(() => {
    state.resolve?.(false);
    setState(prev => ({ ...prev, open: false }));
  }, [state.resolve]);

  const confirmDialog = {
    open: state.open,
    title: state.title,
    message: state.message,
    variant: state.variant,
    confirmLabel: state.confirmLabel,
    cancelLabel: state.cancelLabel,
    onConfirm: handleConfirm,
    onCancel: handleCancel
  };

  return { confirmDialog, confirm };
}
